import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ArrowDownToLine, Sparkles, Ticket, TrendingUp, Wallet, XCircle } from "lucide-react";

const CONFETTI_COLORS = {
  dice: ["#81c784", "#a5d6a7", "#64b5f6", "#ffd54f", "#fff"],
  flip: ["#ffd54f", "#ffca28", "#64b5f6", "#fff8e1", "#fff"],
  numbers: ["#64b5f6", "#81c784", "#ef5350", "#e7b64b", "#fff"],
};

const VARIANT_META = {
  ticket: { icon: Ticket, label: "Ticket locked" },
  invest: { icon: Wallet, label: "Pool share" },
  cashout: { icon: ArrowDownToLine, label: "Cash out" },
  win: { icon: Sparkles, label: "Winner" },
  loss: { icon: XCircle, label: "Round settled" },
  refund: { icon: TrendingUp, label: "Refunded" },
  info: { icon: Sparkles, label: "Update" },
};

const formatCoin = (value) => {
  const n = Math.floor(Number(value) || 0);
  return n.toLocaleString("en-US");
};

const GameGlyph = ({ game, face, side, outcome }) => {
  if (game === "numbers") {
    const n = outcome != null ? Math.floor(Number(outcome)) : null;
    return (
      <div className="bgame-glyph bnums-glyph" aria-hidden>
        <span className="bgame-glyph-face">{n != null && n >= 1 ? n : "?"}</span>
        <span className="bgame-glyph-ring" />
      </div>
    );
  }

  if (game === "dice") {
    const n = face != null ? Math.floor(Number(face)) : null;
    return (
      <div className="bgame-glyph bdice-glyph" aria-hidden>
        <span className="bgame-glyph-face">{n != null && n >= 1 && n <= 6 ? n : "?"}</span>
        <span className="bgame-glyph-ring" />
      </div>
    );
  }

  const letter = side === "head" ? "H" : side === "tail" ? "T" : "?";
  return (
    <div className={`bgame-glyph bflip-glyph${side ? ` side-${side}` : ""}`} aria-hidden>
      <span className="bgame-glyph-face">{letter}</span>
      <span className="bgame-glyph-ring" />
    </div>
  );
};

const formatOutcomeLabel = (outcome, game) => {
  if (outcome == null || outcome === "") return "—";
  if (game === "numbers") {
    const n = Math.floor(Number(outcome));
    if (Number.isFinite(n)) return String(n);
    return String(outcome);
  }
  if (game === "flip") {
    if (outcome === "head") return "Head";
    if (outcome === "tail") return "Tail";
    return String(outcome);
  }
  const n = Math.floor(Number(outcome));
  if (Number.isFinite(n)) return `Face ${n}`;
  return String(outcome);
};

const ConfettiBurst = ({ game, active }) => {
  const colors = CONFETTI_COLORS[game] || CONFETTI_COLORS.flip;
  const pieces = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        id: i,
        left: `${8 + ((i * 17) % 84)}%`,
        delay: `${(i % 6) * 0.07}s`,
        color: colors[i % colors.length],
        rotate: `${(i * 37) % 360}deg`,
        size: i % 3 === 0 ? 7 : 5,
      })),
    [colors]
  );

  if (!active) return null;

  return (
    <div className="bgame-confetti" aria-hidden>
      {pieces.map((piece) => (
        <span
          key={piece.id}
          className="bgame-confetti-piece"
          style={{
            left: piece.left,
            animationDelay: piece.delay,
            backgroundColor: piece.color,
            width: piece.size,
            height: piece.size * 1.4,
            transform: `rotate(${piece.rotate})`,
          }}
        />
      ))}
    </div>
  );
};

const BondGameCelebrationModal = ({
  open = false,
  variant = "info",
  game = "dice",
  title = "",
  subtitle = "",
  amount = null,
  stake = null,
  pick = "",
  outcome = "",
  netPayout = null,
  maxWin = null,
  autoCloseMs = null,
  onClose,
}) => {
  useEffect(() => {
    if (!open || !autoCloseMs || !onClose) return undefined;
    const id = window.setTimeout(onClose, autoCloseMs);
    return () => window.clearTimeout(id);
  }, [open, autoCloseMs, onClose]);

  if (!open) return null;

  const meta = VARIANT_META[variant] || VARIANT_META.ticket;
  const Icon = meta.icon;
  const isWin = variant === "win";
  const isLoss = variant === "loss";
  const isTicket = variant === "ticket";
  const isInvest = variant === "invest" || variant === "cashout";
  const tone = isLoss || variant === "error" ? "loss" : isWin ? "win" : "success";
  const showConfetti = isWin || isTicket || variant === "invest";

  const headline =
    title ||
    (isWin ? "You nailed it!" : isLoss ? "Not this time" : isTicket ? "Ticket secured" : isInvest ? "Share added" : "Done");

  const displayAmount = amount != null ? formatCoin(amount) : null;
  const displayStake = stake != null ? formatCoin(stake) : null;
  const displayNet = netPayout != null ? formatCoin(netPayout) : null;
  const displayMaxWin = maxWin != null ? formatCoin(maxWin) : null;

  const outcomeFace = game === "dice" ? outcome : null;
  const outcomeSide = game === "flip" ? outcome : null;
  const outcomeNumber = game === "numbers" ? outcome : null;

  return createPortal(
    <div
      className={`bgame-celebration-root game-${game} tone-${tone} variant-${variant}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bgame-celebration-title"
    >
      <button type="button" className="bgame-celebration-backdrop" aria-label="Close" onClick={onClose} />

      <div className="bgame-celebration-sheet">
        <ConfettiBurst game={game} active={showConfetti} />

        <div className="bgame-celebration-glow" aria-hidden />
        <div className="bgame-celebration-rays" aria-hidden />

        {isTicket ? (
          <div className="bgame-ticket-stub" aria-hidden>
            <span className="bgame-ticket-notch left" />
            <span className="bgame-ticket-notch right" />
            <span className="bgame-ticket-perforation" />
          </div>
        ) : null}

        <div className="bgame-celebration-badge">
          <Icon size={15} strokeWidth={2.5} />
          <span>{meta.label}</span>
        </div>

        <div className="bgame-celebration-hero">
          <GameGlyph
            game={game}
            face={isWin || isLoss ? outcomeFace : null}
            side={isWin || isLoss ? outcomeSide : null}
            outcome={isWin || isLoss ? outcomeNumber : null}
          />
        </div>

        <h2 id="bgame-celebration-title" className="bgame-celebration-title">
          {headline}
        </h2>

        {subtitle ? <p className="bgame-celebration-subtitle">{subtitle}</p> : null}

        {(pick || outcome || displayStake) && (isWin || isLoss || isTicket) ? (
          <div className="bgame-celebration-stats">
            {pick ? (
              <div className="bgame-stat-row">
                <span>Your pick</span>
                <strong>{pick}</strong>
              </div>
            ) : null}
            {outcome && (isWin || isLoss) ? (
              <div className="bgame-stat-row">
                <span>Result</span>
                <strong>{formatOutcomeLabel(outcome, game)}</strong>
              </div>
            ) : null}
            {displayStake && isTicket ? (
              <div className="bgame-stat-row">
                <span>Stake locked</span>
                <strong>COIN {displayStake}</strong>
              </div>
            ) : null}
            {displayMaxWin && isTicket ? (
              <div className="bgame-stat-row highlight">
                <span>Max win</span>
                <strong>COIN {displayMaxWin}</strong>
              </div>
            ) : null}
          </div>
        ) : null}

        {(displayAmount || displayNet || (isLoss && displayStake)) ? (
          <div className={`bgame-celebration-payout ${tone}`}>
            <span>
              {isWin ? "Net payout" : isLoss ? "Stake lost" : isInvest ? "Amount" : variant === "cashout" ? "Withdrawn" : "Total"}
            </span>
            <strong>
              COIN{" "}
              {isLoss && displayStake
                ? `-${displayStake}`
                : displayNet != null
                  ? displayNet
                  : displayAmount}
            </strong>
            {isWin && stake != null && netPayout != null && netPayout !== stake ? (
              <small>After fees · Stake was COIN {formatCoin(stake)}</small>
            ) : null}
          </div>
        ) : null}

        {isTicket ? (
          <p className="bgame-celebration-hint">Hit <strong>Roll now</strong> before the timer runs out.</p>
        ) : null}

        {autoCloseMs ? (
          <div className="bgame-celebration-progress" style={{ animationDuration: `${autoCloseMs}ms` }} aria-hidden />
        ) : null}

        <button type="button" className="bgame-celebration-btn" onClick={onClose}>
          {isWin || isLoss ? "Play again" : "Continue"}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default BondGameCelebrationModal;
